import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Download, RotateCcw, ImagePlus, User, Store, Package, MapPin, Move } from "lucide-react";

const FILTERS = [
  { key: "original", label: "Original", css: "" },
  { key: "warm",     label: "Warm & cozy",  css: "sepia(0.15) saturate(1.2) brightness(1.05) contrast(1.05)" },
  { key: "vivid",    label: "Vivid",        css: "saturate(1.6) contrast(1.15) brightness(1.05)" },
  { key: "bright",   label: "Bright & airy",css: "brightness(1.15) contrast(1.05) saturate(0.95)" },
  { key: "moody",    label: "Moody",        css: "contrast(1.25) brightness(0.92) saturate(1.1)" },
  { key: "bw",       label: "Black & white",css: "grayscale(1) contrast(1.1)" },
  { key: "vintage",  label: "Vintage",      css: "sepia(0.4) saturate(1.1) contrast(0.95) brightness(1.05)" },
  { key: "crisp",    label: "Crisp",        css: "contrast(1.15) saturate(1.05) brightness(1.02)" },
];

// Site + social presets. "apply" says which profile field the resized image can be pushed to.
const SIZE_PRESETS = [
  // --- Site fields (cake pop talk) ---
  { key: "avatar",       label: "Profile Avatar 200×200",     w: 200,  h: 200,  apply: "avatar",  group: "Site fields" },
  { key: "banner",       label: "Store Banner 1200×400",       w: 1200, h: 400,  apply: "banner",  group: "Site fields" },
  { key: "product",      label: "Product Listing 800×800",     w: 800,  h: 800,  apply: null,      group: "Site fields" },
  { key: "map_thumb",    label: "Map Pin Thumbnail 100×100",   w: 100,  h: 100,  apply: "avatar",  group: "Site fields" },
  // --- Common exports ---
  { key: "original",     label: "Original size",               w: null, h: null, apply: null,      group: "Export" },
  { key: "instagram_sq", label: "Instagram square 1080×1080",  w: 1080, h: 1080, apply: null,      group: "Export" },
  { key: "instagram_p",  label: "Instagram portrait 1080×1350",w: 1080, h: 1350, apply: null,      group: "Export" },
  { key: "instagram_st", label: "Instagram story 1080×1920",   w: 1080, h: 1920, apply: null,      group: "Export" },
  { key: "facebook_c",   label: "Facebook cover 1200×630",     w: 1200, h: 630,  apply: null,      group: "Export" },
  { key: "pinterest",    label: "Pinterest pin 1000×1500",     w: 1000, h: 1500, apply: null,      group: "Export" },
  { key: "web_1200",     label: "Web 1200px wide (keep ratio)",w: 1200, h: 0,    apply: null,      group: "Export" },
  { key: "thumb_400",    label: "Thumbnail 400px wide",        w: 400,  h: 0,    apply: null,      group: "Export" },
];

export default function PhotoEnhancer() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [src, setSrc] = useState(null);
  const [filter, setFilter] = useState(FILTERS[0]);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturate, setSaturate] = useState(100);
  const [warmth, setWarmth] = useState(0);
  const [sizePreset, setSizePreset] = useState(SIZE_PRESETS[0]);
  const [naturalDims, setNaturalDims] = useState({ w: 0, h: 0 });
  const [offset, setOffset] = useState({ x: 0.5, y: 0.5 }); // crop center as 0..1 of source
  const [applying, setApplying] = useState(false);
  const imgRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { if (!loading && (!user || user === false)) nav("/login"); }, [user, loading, nav]);

  const onPick = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please pick an image"); return; }
    setSrc(URL.createObjectURL(file));
    reset();
    setOffset({ x: 0.5, y: 0.5 });
  };
  const reset = () => { setFilter(FILTERS[0]); setBrightness(100); setContrast(100); setSaturate(100); setWarmth(0); };

  const fullFilter = `${filter.css} brightness(${brightness}%) contrast(${contrast}%) saturate(${saturate}%) sepia(${warmth}%)`;

  // Compute the crop rectangle in NATURAL image coordinates for the current preset + offset.
  const computeCropRect = (natW, natH, preset) => {
    if (!preset.w || !preset.h) return { sx: 0, sy: 0, sw: natW, sh: natH, outW: preset.w || natW, outH: preset.h || natH, exact: false };
    const srcAspect = natW / natH;
    const dstAspect = preset.w / preset.h;
    let sw = natW, sh = natH;
    if (srcAspect > dstAspect) { sw = natH * dstAspect; }
    else                       { sh = natW / dstAspect; }
    // Position the crop centered by (offset.x, offset.y) — clamped inside the image
    let sx = offset.x * natW - sw / 2;
    let sy = offset.y * natH - sh / 2;
    sx = Math.max(0, Math.min(natW - sw, sx));
    sy = Math.max(0, Math.min(natH - sh, sy));
    return { sx, sy, sw, sh, outW: preset.w, outH: preset.h, exact: true };
  };

  const renderToBlob = () => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      setNaturalDims({ w: img.naturalWidth, h: img.naturalHeight });
      const preset = sizePreset;
      let outW = img.naturalWidth, outH = img.naturalHeight;
      let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
      if (preset.w && preset.h) {
        const rect = computeCropRect(img.naturalWidth, img.naturalHeight, preset);
        ({ sx, sy, sw, sh, outW, outH } = rect);
      } else if (preset.w && !preset.h) {
        outW = preset.w;
        outH = Math.round(preset.w / (img.naturalWidth / img.naturalHeight));
      } else if (!preset.w && preset.h) {
        outH = preset.h;
        outW = Math.round(preset.h * (img.naturalWidth / img.naturalHeight));
      }
      const canvas = document.createElement("canvas");
      canvas.width = outW; canvas.height = outH;
      const ctx = canvas.getContext("2d");
      ctx.filter = fullFilter;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
      canvas.toBlob((blob) => resolve({ blob, outW, outH }), "image/jpeg", 0.92);
    };
    img.src = src;
  });

  const download = async () => {
    if (!src) return;
    const { blob, outW, outH } = await renderToBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `enhanced-${sizePreset.key}-${outW}x${outH}-${Date.now()}.jpg`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${outW}×${outH}`);
  };

  const applyTo = async (kind) => {
    setApplying(true);
    try {
      const { blob, outW, outH } = await renderToBlob();
      const fd = new FormData();
      fd.append("file", new File([blob], `${kind}-${outW}x${outH}.jpg`, { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const path = data.path;
      if (kind === "avatar") {
        await api.patch("/auth/me", { avatar_url: path });
        toast.success("Profile avatar updated 🎉");
      } else if (kind === "banner") {
        await api.patch("/seller/me", {
          business_name: user.seller_profile?.business_name,
          contact_email: user.seller_profile?.contact_email || user.email,
          banner_url: path,
        });
        toast.success("Store banner updated 🎉");
      }
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  // ---- Drag to reposition crop area on the source preview ----
  const dragState = useRef({ active: false, startX: 0, startY: 0, startOffset: { x: 0.5, y: 0.5 }, imgW: 0, imgH: 0 });
  const onImgPointerDown = (e) => {
    if (!sizePreset.w || !sizePreset.h) return; // no drag needed for keep-ratio presets
    const rect = imgRef.current.getBoundingClientRect();
    dragState.current = { active: true, startX: e.clientX, startY: e.clientY, startOffset: { ...offset }, imgW: rect.width, imgH: rect.height };
    imgRef.current.setPointerCapture?.(e.pointerId);
  };
  const onImgPointerMove = (e) => {
    const s = dragState.current;
    if (!s.active) return;
    const dx = (e.clientX - s.startX) / s.imgW;
    const dy = (e.clientY - s.startY) / s.imgH;
    // Drag moves the CROP frame, i.e. we shift the crop center in the opposite direction of drag.
    setOffset({
      x: Math.max(0, Math.min(1, s.startOffset.x - dx)),
      y: Math.max(0, Math.min(1, s.startOffset.y - dy)),
    });
  };
  const onImgPointerUp = () => { dragState.current.active = false; };

  // ---- Crop overlay rectangle expressed as % of the displayed image (for visual guidance) ----
  const overlayRect = (() => {
    if (!naturalDims.w || !sizePreset.w || !sizePreset.h) return null;
    const srcAspect = naturalDims.w / naturalDims.h;
    const dstAspect = sizePreset.w / sizePreset.h;
    let wPct = 100, hPct = 100;
    if (srcAspect > dstAspect) wPct = (dstAspect / srcAspect) * 100;
    else                       hPct = (srcAspect / dstAspect) * 100;
    // Center around offset
    let leftPct = offset.x * 100 - wPct / 2;
    let topPct = offset.y * 100 - hPct / 2;
    leftPct = Math.max(0, Math.min(100 - wPct, leftPct));
    topPct = Math.max(0, Math.min(100 - hPct, topPct));
    return { leftPct, topPct, wPct, hPct };
  })();

  const canApplyAvatar = sizePreset.apply === "avatar";
  const canApplyBanner = sizePreset.apply === "banner" && user?.is_seller;

  if (loading || !user) return null;
  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Camera className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} /><span>Marketing tool</span></div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">Photo <span style={{ color: "#F4A83B" }}>Enhancer</span></h1>
            <p className="mt-2 text-[#857163]">Enhance, resize, and export your photos. Pick a preset to auto-crop for site fields like your Profile Avatar or Store Banner — then drag to reposition the crop.</p>
          </div>
          {src && <button onClick={reset} className="cp-btn-ghost text-sm inline-flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>}
        </div>
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div>
            <div
              onClick={() => !src && inputRef.current?.click()}
              className="cp-card overflow-hidden min-h-[400px] grid place-items-center bg-[#F9EFE6] relative"
              style={{ cursor: src && sizePreset.w && sizePreset.h ? "grab" : "pointer" }}
            >
              <input ref={inputRef} type="file" accept="image/*" onChange={(e) => onPick(e.target.files?.[0])} className="hidden" data-testid="file-input" />
              {src ? (
                <div className="relative select-none" style={{ touchAction: "none" }}>
                  <img
                    ref={imgRef}
                    src={src}
                    alt=""
                    className="max-w-full max-h-[560px] block"
                    style={{ filter: fullFilter }}
                    data-testid="preview-img"
                    onLoad={(e) => setNaturalDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                    onPointerDown={onImgPointerDown}
                    onPointerMove={onImgPointerMove}
                    onPointerUp={onImgPointerUp}
                    onPointerCancel={onImgPointerUp}
                    draggable={false}
                  />
                  {overlayRect && (
                    <>
                      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: `inset 0 0 0 9999px rgba(0,0,0,0.35)`, clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 ${overlayRect.topPct}%, ${overlayRect.leftPct}% ${overlayRect.topPct}%, ${overlayRect.leftPct}% ${overlayRect.topPct + overlayRect.hPct}%, ${overlayRect.leftPct + overlayRect.wPct}% ${overlayRect.topPct + overlayRect.hPct}%, ${overlayRect.leftPct + overlayRect.wPct}% ${overlayRect.topPct}%, 0 ${overlayRect.topPct}%)` }} />
                      <div
                        className="absolute pointer-events-none border-2 border-white shadow-lg"
                        style={{ left: `${overlayRect.leftPct}%`, top: `${overlayRect.topPct}%`, width: `${overlayRect.wPct}%`, height: `${overlayRect.hPct}%`, borderRadius: sizePreset.key === "avatar" || sizePreset.key === "map_thumb" ? "50%" : 0 }}
                        data-testid="crop-overlay"
                      />
                      <div className="absolute left-2 top-2 pointer-events-none inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-white/90 text-[#3B2C24] shadow"><Move className="w-3 h-3" /> Drag to reposition</div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-20 text-[#857163]"><ImagePlus className="w-12 h-12 mx-auto opacity-40 mb-2" /><div className="font-heading text-xl text-[#3B2C24]">Click to upload a photo</div><div className="text-sm">JPEG, PNG, WEBP</div></div>
              )}
            </div>

            {src && (
              <>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button onClick={() => inputRef.current?.click()} className="cp-btn-ghost text-sm inline-flex items-center gap-1.5"><ImagePlus className="w-4 h-4" /> Change photo</button>
                  <div className="flex items-center gap-2 ml-auto flex-wrap">
                    <label className="text-xs text-[#857163] whitespace-nowrap">Save as</label>
                    <select
                      value={sizePreset.key}
                      onChange={(e) => setSizePreset(SIZE_PRESETS.find((s) => s.key === e.target.value) || SIZE_PRESETS[0])}
                      className="cp-input !py-2 !text-xs !w-auto"
                      data-testid="size-preset-select"
                    >
                      {["Site fields", "Export"].map((g) => (
                        <optgroup key={g} label={g}>
                          {SIZE_PRESETS.filter((s) => s.group === g).map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <button onClick={download} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90" style={{ background: "#F4A83B" }} data-testid="download-btn"><Download className="w-4 h-4" /> Download</button>
                  </div>
                </div>
                {(canApplyAvatar || canApplyBanner) && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="apply-actions">
                    {canApplyAvatar && (
                      <button onClick={() => applyTo("avatar")} disabled={applying} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60" style={{ background: "#5E7A2E" }} data-testid="apply-avatar-btn">
                        <User className="w-4 h-4" /> {applying ? "Applying…" : "Apply to Profile Avatar"}
                      </button>
                    )}
                    {canApplyBanner && (
                      <button onClick={() => applyTo("banner")} disabled={applying} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60" style={{ background: "#2F80ED" }} data-testid="apply-banner-btn">
                        <Store className="w-4 h-4" /> {applying ? "Applying…" : "Apply to Store Banner"}
                      </button>
                    )}
                  </div>
                )}
                {sizePreset.key === "product" && (
                  <div className="mt-2 text-xs text-[#857163]" data-testid="product-note"><Package className="w-3 h-3 inline mr-1" />Download this file, then attach it to a product in your Seller Dashboard.</div>
                )}
                {sizePreset.key === "map_thumb" && (
                  <div className="mt-2 text-xs text-[#857163]" data-testid="map-note"><MapPin className="w-3 h-3 inline mr-1" />Your map pin uses your profile avatar — clicking "Apply to Profile Avatar" updates both.</div>
                )}
              </>
            )}
          </div>
          {src && (
            <div className="cp-card p-4 space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#857163] mb-2">Presets</div>
                <div className="grid grid-cols-2 gap-2">
                  {FILTERS.map((f) => (
                    <button key={f.key} onClick={() => setFilter(f)} className={`px-3 py-2 rounded-2xl text-xs font-medium border transition ${filter.key === f.key ? "bg-[#3B2C24] text-white border-[#3B2C24]" : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"}`} data-testid={`filter-${f.key}`}>{f.label}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-3 pt-3 border-t border-[#EBDCD1]">
                <Slider label="Brightness" value={brightness} onChange={setBrightness} min={50} max={150} testid="brightness" />
                <Slider label="Contrast" value={contrast} onChange={setContrast} min={50} max={150} testid="contrast" />
                <Slider label="Saturation" value={saturate} onChange={setSaturate} min={0} max={200} testid="saturate" />
                <Slider label="Warmth" value={warmth} onChange={setWarmth} min={0} max={80} testid="warmth" />
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
function Slider({ label, value, onChange, min, max, testid }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1"><span className="text-[#857163]">{label}</span><span className="text-[#3B2C24] font-semibold">{value}%</span></div>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(parseInt(e.target.value))} className="w-full accent-[#F4A83B]" data-testid={`slider-${testid}`} />
    </div>
  );
}
